/**
 * QueryBuilder Component
 * Recursive component for building complex query criteria with AND/OR groups
 * Now with field-aware value inputs based on field metadata
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  getZuoraEntitlementValues,
  ZUORA_FIELD_KIND,
  type ZuoraEntitlementValue,
} from "../../services/zuoraEntitlements.service";
import type {
  QueryGroup,
  QueryCondition,
  QueryField,
  FieldOperator,
  FieldOption,
} from "../../types/list.types";
import {
  FIELD_OPTIONS,
  FIELD_METADATA,
  OPERATOR_OPTIONS,
  CONTACT_TYPE_OPTIONS,
  CONTACT_STATUS_OPTIONS,
  CONTACT_EVENT_TYPE_OPTIONS,
  MEMBER_STATUS_OPTIONS,
  EMAIL_QUALITY_OPTIONS,
  ZUORA_ACCOUNT_REGION_OPTIONS,
  ZUORA_ACCOUNT_TYPE_OPTIONS,
  ZUORA_ACCOUNT_STATUS_OPTIONS,
} from "../../types/list.types";
import { US_STATES as US_STATES_WITH_CODES } from "../../types/event.types";
import { supabase } from "../../services/supabase";
import { fetchAllRows, pageByNameThenId } from "../../utils/fetchAllRows";
import { getAttributeDefinitions } from "../../services/contactAttributes.service";
import type { AttributeDefinition } from "../../types/contactAttribute.types";
import { getCustomAttributeOverride } from "./customAttributeOverrides";
import { ProximityValueInput } from "./ProximityValueInput";

// Map a contact_attribute_definitions.data_type to the FieldOption type
// the QueryBuilder uses to drive input rendering and operator filtering.
// 'select' uses value_text storage server-side, so map to 'text' in the UI
// for now (predefined-option dropdown is a polish item; free text works).
function customAttrDataTypeToFieldOptionType(
  dataType: string,
): FieldOption["type"] {
  switch (dataType) {
    case "boolean":
      return "boolean";
    case "number":
      return "number";
    case "date":
      return "date";
    case "select":
    case "text":
    default:
      return "text";
  }
}

export interface SegmentOption {
  value: string;
  label: string;
}

interface QueryBuilderProps {
  group: QueryGroup;
  depth?: number;
  onChange: (group: QueryGroup) => void;
  onRemove?: () => void;
  /** Fields to exclude from the field dropdown (e.g. behavioral fields unsupported by SQL) */
  excludeFields?: string[];
}

// Field type determines what input to render
type FieldType =
  | "text"
  | "boolean"
  | "date"
  | "select"
  | "number"
  | "behavioral"
  | "proximity"
  | "zuora";

// Behavioral fields that query contact_events instead of contacts columns
const BEHAVIORAL_FIELDS: QueryField[] = [
  "event_performed",
  "event_not_performed",
];

function isBehavioralField(field: QueryField): boolean {
  return BEHAVIORAL_FIELDS.includes(field);
}

// Module-level cache for active custom attribute definitions. Populated
// once by useCustomAttributeFields and read synchronously by getFieldType
// for custom:<slug> field lookups. Mirrors the _cachedSegmentOptions
// pattern already in this file.
let _cachedCustomAttributeDefs: AttributeDefinition[] | null = null;

function getCustomAttrFieldType(slug: string): FieldType | null {
  if (!_cachedCustomAttributeDefs) {
    return null;
  }
  const def = _cachedCustomAttributeDefs.find(
    (d) => d.slug === slug && d.is_active,
  );
  if (!def) {
    return null;
  }
  switch (def.data_type) {
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "number":
      return "number";
    case "select":
    case "text":
    default:
      return "text";
  }
}

// Get the input type for a field
function getFieldType(field: QueryField): FieldType {
  if (isBehavioralField(field)) {
    return "behavioral";
  }
  // Custom contact attribute — look up the data_type from the cached
  // definitions. Returns 'text' as a safe fallback if the cache has not
  // populated yet (rare; first render before the fetch completes).
  if (typeof field === "string" && field.startsWith("custom:")) {
    const slug = field.slice("custom:".length);
    const ftype = getCustomAttrFieldType(slug);
    return ftype ?? "text";
  }
  const metadata = FIELD_METADATA[field];
  if (metadata) {
    if (metadata.type === "boolean") {
      return "boolean";
    }
    if (metadata.type === "date") {
      return "date";
    }
    if (
      metadata.type === "select" ||
      metadata.type === "multi-select" ||
      metadata.type === "uuid" ||
      metadata.type === "array"
    ) {
      return "select";
    }
    if (metadata.type === "number") {
      return "number";
    }
    if (metadata.type === "proximity") {
      return "proximity";
    }
    if (metadata.type === "zuora") {
      return "zuora";
    }
  }
  return "text";
}

// Coerce a condition value to a scalar suitable for a native <input>/<select> value prop. Non-scalar
// values (arrays, tuples, booleans) are handled by their own branches, so "" is the safe fallback here.
function scalarInputValue(v: QueryCondition["value"]): string | number {
  return typeof v === "string" || typeof v === "number" ? v : "";
}

// Get options for select fields
function getFieldSelectOptions(
  field: QueryField,
  segmentOptions: SegmentOption[] = [],
  tagOptions: SegmentOption[] = [],
): { value: string; label: string }[] {
  switch (field) {
    case "contact_type":
      return CONTACT_TYPE_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "status":
      return CONTACT_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "state":
      // Save 2-letter code as the value; show full name as the label.
      // The DB compiler (build_strict_segment_sql) normalizes either form
      // via _normalize_state_in_criteria_leaf, but writing codes means
      // saved lists are self-consistent and the impossible-AND detector
      // below sees the same shape on every condition.
      return US_STATES_WITH_CODES.map((s) => ({
        value: s.code,
        label: s.name,
      }));
    case "member_status":
      return MEMBER_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "email_quality":
      return EMAIL_QUALITY_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    // Zuora Account canonical option lists (Phase 3). The UI emits the exact
    // canonical stored value so shorthand (e.g. "Colorado") is never persisted.
    case "account_region":
      return ZUORA_ACCOUNT_REGION_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "account_type":
      return ZUORA_ACCOUNT_TYPE_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "account_status":
      return ZUORA_ACCOUNT_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      }));
    case "segment_membership":
      return segmentOptions;
    case "tags":
      return tagOptions;
    default:
      return [];
  }
}

// Get operators valid for a field type. `field` is optional and only used to
// narrow the operator set for specific virtual fields (e.g. email_quality
// supports equals/not_equals/in/not_in but NOT is_null/is_not_null — its value
// is always one of the 4 buckets, and the SQL compiler rejects null operators).
function getOperatorsForFieldType(
  fieldType: FieldType,
  field?: QueryField,
): { value: FieldOperator; label: string }[] {
  const allOperators = OPERATOR_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  // Zuora entitlements: only these shapes reach a supported compiler branch. Labels say
  // "current" out loud, because matching a plan a member no longer holds is the surprise.
  if (field === "zuora_plan_name" || field === "product_names") {
    return [
      { value: "in" as FieldOperator, label: "is any of (current)" },
      { value: "not_in" as FieldOperator, label: "is none of (current)" },
      { value: "is_not_null" as FieldOperator, label: "has any current" },
      { value: "is_null" as FieldOperator, label: "has none current" },
    ];
  }

  if (field === "email_quality") {
    return allOperators.filter((o) =>
      ["equals", "not_equals", "in", "not_in"].includes(o.value),
    );
  }

  // Email-open engagement booleans are virtual (no contacts column). The strict
  // compiler only accepts equals/not_equals with a boolean value; is_null/is_not_null
  // (offered generically for boolean) would fail closed server-side, so hide them.
  if (field === "opened_email_30d" || field === "opened_email_90d") {
    return allOperators.filter((o) =>
      ["equals", "not_equals"].includes(o.value),
    );
  }

  // Zuora Account Last Invoice Date (Phase 3): the standard date operators PLUS the
  // account-level "In the previous N calendar days" operator. Only this field exposes
  // previous_calendar_days (other date fields route it to a fail-closed compiler error).
  if (field === "last_invoice_date") {
    return allOperators.filter((o) =>
      [
        "equals",
        "not_equals",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
        "between",
        "days_ago",
        "days_in_future",
        "previous_calendar_days",
        "is_null",
        "is_not_null",
      ].includes(o.value),
    );
  }

  switch (fieldType) {
    case "boolean":
      return allOperators.filter((o) =>
        ["equals", "not_equals", "is_null", "is_not_null"].includes(o.value),
      );
    case "number":
      return allOperators.filter((o) =>
        [
          "equals",
          "not_equals",
          "greater_than",
          "less_than",
          "greater_than_or_equal",
          "less_than_or_equal",
          "between",
          "is_null",
          "is_not_null",
        ].includes(o.value),
      );
    case "date":
      // Date adds the relative-time operators days_ago / days_in_future
      // on top of the numeric ones (the SQL compiler already supported
      // them since Pardot 2A; frontend wired up 2026-05-20).
      return allOperators.filter((o) =>
        [
          "equals",
          "not_equals",
          "greater_than",
          "less_than",
          "greater_than_or_equal",
          "less_than_or_equal",
          "between",
          "days_ago",
          "days_in_future",
          "is_null",
          "is_not_null",
        ].includes(o.value),
      );
    case "select":
      return allOperators.filter((o) =>
        [
          "equals",
          "not_equals",
          "in",
          "not_in",
          "is_null",
          "is_not_null",
        ].includes(o.value),
      );
    case "behavioral":
      return [
        { value: "equals" as FieldOperator, label: "Ever" },
        { value: "within_days" as FieldOperator, label: "Within Last X Days" },
      ];
    case "proximity":
      return allOperators.filter((o) =>
        [
          "within_radius_of_zip",
          "within_radius_of_org",
          "within_radius_of_location",
          "within_radius_of_event",
        ].includes(o.value),
      );
    default:
      return allOperators;
  }
}

/**
 * Detect "impossible AND" criteria: 2+ direct AND'd conditions on the same
 * scalar field with operator='equals' and different values can never all
 * be true. The classic case is the production-user "Insurance States"
 * issue: `state equals IL AND state equals LA AND state equals MI` —
 * meant as OR but built as AND, matches 0 contacts.
 *
 * Returns the list of offending field names so the UI can surface them.
 * Only direct children of the group are scanned; nested groups are
 * evaluated recursively in their own render via this same component.
 *
 * Scalar field types: text, number, boolean, uuid, date, select. Excluded:
 * array (tags can have multiple equals — contact has both), behavioral.
 */
function detectImpossibleAndFields(group: QueryGroup): string[] {
  if (group.operator !== "AND") {
    return [];
  }

  const equalsByField = new Map<string, Set<string>>();
  for (const item of group.conditions) {
    if (isQueryGroup(item)) {
      continue;
    } // nested groups validated separately
    const c = item as QueryCondition;
    if (c.operator !== "equals") {
      continue;
    }
    const fieldType = getFieldType(c.field);
    // Array + behavioral: multiple equals can both be true (e.g., contact
    // has both tag A AND tag B). Skip.
    if (fieldType === "array" || fieldType === "behavioral") {
      continue;
    }
    const valueKey =
      c.value === null || c.value === undefined ? "" : String(c.value);
    if (!equalsByField.has(c.field)) {
      equalsByField.set(c.field, new Set());
    }
    equalsByField.get(c.field)!.add(valueKey);
  }

  const offending: string[] = [];
  for (const [field, values] of equalsByField) {
    if (values.size >= 2) {
      offending.push(field);
    }
  }
  return offending;
}

const isQueryGroup = (
  item: QueryCondition | QueryGroup,
): item is QueryGroup => {
  return "operator" in item && "conditions" in item;
};

// Shared segment options — fetched once at the top-level QueryBuilder
let _cachedSegmentOptions: SegmentOption[] | null = null;
let _cachedTagOptions: SegmentOption[] | null = null;

type CustomAttrFetchStatus = "loading" | "loaded" | "error";

/**
 * Fetch active contact_attribute_definitions once and cache module-wide.
 * Returns the derived FieldOption[] AND a status flag so callers can
 * distinguish "loaded with no custom attrs" from "still loading" from
 * "fetch errored". Critical for getFieldType correctness: a stale-cache
 * fallback to 'text' would let a user pick the wrong operator for a
 * boolean/date custom attribute and save it; the strict compiler would
 * then reject the saved criteria at refresh time. Audit AI 2026-05-20
 * flagged this as a should-fix before prod.
 */
function useCustomAttributeFieldOptions(): {
  options: FieldOption[];
  status: CustomAttrFetchStatus;
} {
  const initialStatus: CustomAttrFetchStatus = _cachedCustomAttributeDefs
    ? "loaded"
    : "loading";
  const [status, setStatus] = useState<CustomAttrFetchStatus>(initialStatus);
  const [options, setOptions] = useState<FieldOption[]>(
    _cachedCustomAttributeDefs
      ? _cachedCustomAttributeDefs
          .filter((d) => d.is_active)
          .map((d) => ({
            value: `custom:${d.slug}` as QueryField,
            label: `Attribute: ${d.name}`,
            type: customAttrDataTypeToFieldOptionType(d.data_type),
          }))
      : [],
  );

  useEffect(() => {
    if (_cachedCustomAttributeDefs) {
      return;
    }
    getAttributeDefinitions()
      .then((defs) => {
        _cachedCustomAttributeDefs = defs;
        const opts = defs
          .filter((d) => d.is_active)
          .map((d) => ({
            value: `custom:${d.slug}` as QueryField,
            label: `Attribute: ${d.name}`,
            type: customAttrDataTypeToFieldOptionType(d.data_type),
          }));
        setOptions(opts);
        setStatus("loaded");
      })
      .catch((err) => {
        // Surface explicitly to the UI so a stale cache doesn't degrade
        // existing custom: criteria to text operators.
        console.warn(
          "QueryBuilder: failed to load custom attribute definitions",
          err,
        );
        setStatus("error");
      });
  }, []);

  return { options, status };
}

function useSegmentOptions(): SegmentOption[] {
  const [options, setOptions] = useState<SegmentOption[]>(
    _cachedSegmentOptions || [],
  );

  useEffect(() => {
    if (_cachedSegmentOptions) {
      return;
    }
    // 2026-05-29: include dynamic lists. The backend segment-membership
    // compiler (build_query_criteria_sql + build_strict_segment_sql,
    // migration 2026-05-29-001) reads materialized list_contacts for
    // both static and dynamic — for dynamic lists the operator-controlled
    // refresh_list_count cadence determines membership freshness.
    // Surface the is_dynamic flag in the label so operators understand
    // which kind they're picking. Include contact_count so they can
    // see size at a glance.
    // Paged: PROD has more segments than PostgREST's 1000-row cap.
    fetchAllRows<any>((from, to) =>
      pageByNameThenId(
        supabase
          .from("lists")
          .select("id, name, is_dynamic, contact_count")
          .is("archived_at", null),
        from,
        to,
      ),
    )
      .catch((err) => {
        console.error("Failed to load segment options:", err);
        return [] as any[];
      })
      .then((data) => {
        const opts = (data || []).map((s: any) => {
          const kind = s.is_dynamic ? "Dynamic" : "Static";
          const count =
            typeof s.contact_count === "number"
              ? `, ${s.contact_count.toLocaleString()} contacts`
              : "";
          return {
            value: s.id,
            label: `${s.name} (${kind}${count})`,
          };
        });
        _cachedSegmentOptions = opts;
        setOptions(opts);
      });
  }, []);

  return options;
}

function useTagOptions(): SegmentOption[] {
  const [options, setOptions] = useState<SegmentOption[]>(
    _cachedTagOptions || [],
  );

  useEffect(() => {
    if (_cachedTagOptions) {
      return;
    }

    Promise.all([
      supabase.from("contact_tags").select("tag").order("tag").limit(1000),
      supabase.from("form_handlers").select("completion_actions"),
    ]).then(([contactTagsResult, formHandlersResult]) => {
      const tags = new Set<string>();

      (contactTagsResult.data || []).forEach((row: any) => {
        if (typeof row.tag === "string" && row.tag.trim()) {
          tags.add(row.tag.trim());
        }
      });

      (formHandlersResult.data || []).forEach((row: any) => {
        const configuredTags = row.completion_actions?.add_tags?.tags;
        if (Array.isArray(configuredTags)) {
          configuredTags.forEach((tag: unknown) => {
            if (typeof tag === "string" && tag.trim()) {
              tags.add(tag.trim());
            }
          });
        }
      });

      const opts = Array.from(tags)
        .sort((a, b) => a.localeCompare(b))
        .map((tag) => ({ value: tag, label: tag }));

      _cachedTagOptions = opts;
      setOptions(opts);
    });
  }, []);

  return options;
}

const QueryBuilder: React.FC<QueryBuilderProps> = ({
  group,
  depth = 0,
  onChange,
  onRemove,
  excludeFields,
}) => {
  const segmentOptions = useSegmentOptions();
  const tagOptions = useTagOptions();

  const toggleOperator = () => {
    onChange({
      ...group,
      operator: group.operator === "AND" ? "OR" : "AND",
    });
  };

  const addCondition = () => {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        {
          field: "contact_type",
          operator: "equals",
          value: "",
        } as QueryCondition,
      ],
    });
  };

  const addGroup = () => {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        { operator: "AND", conditions: [] } as QueryGroup,
      ],
    });
  };

  const updateCondition = (
    index: number,
    condition: QueryCondition | QueryGroup,
  ) => {
    const newConditions = [...group.conditions];
    newConditions[index] = condition;
    onChange({ ...group, conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    onChange({
      ...group,
      conditions: group.conditions.filter((_, i) => i !== index),
    });
  };

  return (
    <div
      className={`space-y-3 ${depth > 0 ? "ml-6 pl-4 border-l-2 border-primary-300" : ""}`}
    >
      {/* Group Header */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleOperator}
          className={`px-3 py-1 rounded font-semibold text-sm ${
            group.operator === "AND"
              ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
              : "bg-purple-100 text-purple-700 hover:bg-purple-200"
          }`}
        >
          {group.operator}
        </button>
        <span className="text-sm text-gray-500">
          {group.conditions.length} condition
          {group.conditions.length !== 1 ? "s" : ""}
        </span>
        {depth > 0 && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Remove Group
          </button>
        )}
      </div>

      {/* Impossible-AND warning. Soft amber, not a hard block — the
          marketer might still want to save partial criteria mid-edit
          and come back. Surface the issue, let them act. */}
      {(() => {
        const offending = detectImpossibleAndFields(group);
        if (offending.length === 0) {
          return null;
        }
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-900">
            <span className="font-semibold">Impossible AND condition:</span>{" "}
            multiple <code className="font-mono">equals</code> conditions on{" "}
            {offending.map((f, i) => (
              <span key={f}>
                {i > 0 && ", "}
                <code className="font-mono">{f}</code>
              </span>
            ))}{" "}
            can never all be true at once. A contact's{" "}
            {offending.length === 1 ? offending[0] : "field"} can only have one
            value. Switch the AND to OR, or use the{" "}
            <code className="font-mono">in</code> operator with a list of
            values.
          </div>
        );
      })()}

      {/* Conditions */}
      <div className="space-y-2">
        {group.conditions.map((condition, index) => (
          <div key={index}>
            {isQueryGroup(condition) ? (
              <QueryBuilder
                group={condition}
                depth={depth + 1}
                onChange={(updatedGroup) =>
                  updateCondition(index, updatedGroup)
                }
                onRemove={() => removeCondition(index)}
                excludeFields={excludeFields}
              />
            ) : (
              <ConditionRow
                condition={condition}
                onChange={(updatedCondition) =>
                  updateCondition(index, updatedCondition)
                }
                onRemove={() => removeCondition(index)}
                segmentOptions={segmentOptions}
                tagOptions={tagOptions}
                excludeFields={excludeFields}
              />
            )}
          </div>
        ))}
      </div>

      {/* Add Buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={addCondition}
          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
        >
          + Add Condition
        </button>
        <button
          type="button"
          onClick={addGroup}
          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
        >
          + Add Group
        </button>
      </div>
    </div>
  );
};

interface ConditionRowProps {
  condition: QueryCondition;
  onChange: (condition: QueryCondition) => void;
  onRemove: () => void;
  segmentOptions?: SegmentOption[];
  tagOptions?: SegmentOption[];
  excludeFields?: string[];
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  onChange,
  onRemove,
  segmentOptions = [],
  tagOptions = [],
  excludeFields,
}) => {
  // Dynamic custom-attribute fields ("Attribute: <name>" entries) appended
  // to the static FIELD_OPTIONS so marketers can target by Pardot/SF
  // attributes. Compiled server-side via custom:<slug> in build_strict_
  // segment_sql (migration 2026-05-20-001).
  const { options: customFieldOptions, status: customAttrStatus } =
    useCustomAttributeFieldOptions();
  const allFieldOptions: FieldOption[] = useMemo(
    () => [...FIELD_OPTIONS, ...customFieldOptions],
    [customFieldOptions],
  );
  const isCustomField =
    typeof condition.field === "string" &&
    condition.field.startsWith("custom:");
  // For an existing custom: condition we'd otherwise fall back to 'text'
  // operator filtering while definitions load, exposing wrong operators
  // (e.g. 'contains' on a boolean attribute). Audit AI 2026-05-20 flagged.
  const customAttrLoading = isCustomField && customAttrStatus === "loading";
  const customAttrErrored = isCustomField && customAttrStatus === "error";
  const fieldType = getFieldType(condition.field);
  const operators = getOperatorsForFieldType(fieldType, condition.field);
  const selectOptions = getFieldSelectOptions(
    condition.field,
    segmentOptions,
    tagOptions,
  );

  const handleFieldChange = (field: QueryField) => {
    // Reset value and operator when field changes
    const newFieldType = getFieldType(field);
    const newOperators = getOperatorsForFieldType(newFieldType, field);
    const defaultOperator =
      newOperators.length > 0 ? newOperators[0].value : "equals";
    // Proximity fields default to an empty anchor + a sensible radius; others start blank.
    const defaultValue: QueryCondition["value"] =
      newFieldType === "proximity" ? ["", 20] : "";
    onChange({
      ...condition,
      field,
      operator: defaultOperator,
      value: defaultValue,
    });
  };

  const handleOperatorChange = (operator: FieldOperator) => {
    // Clear value when changing to null operators
    if (["is_null", "is_not_null"].includes(operator)) {
      onChange({ ...condition, operator, value: null });
    } else if (
      operator === "within_radius_of_zip" ||
      operator === "within_radius_of_org" ||
      operator === "within_radius_of_location" ||
      operator === "within_radius_of_event"
    ) {
      // Switching among proximity operators changes the anchor KIND (ZIP vs
      // org/location/event id), so reset the anchor but preserve the radius.
      const prevMiles =
        Array.isArray(condition.value) && condition.value.length === 2
          ? Number(condition.value[1])
          : NaN;
      onChange({
        ...condition,
        operator,
        value: [
          "",
          Number.isFinite(prevMiles) && prevMiles > 0 ? prevMiles : 20,
        ],
      });
    } else if (
      operator === "days_ago" ||
      operator === "days_in_future" ||
      operator === "previous_calendar_days"
    ) {
      // Switching INTO a day-count operator: the SQL compiler will build an
      // interval / calendar-day window from an integer day count. Any existing
      // date-string value (e.g. '2026-05-20') would be invalid. Force a sane
      // numeric default. Audit AI 2026-05-20 re-check #2; extended for
      // previous_calendar_days (Phase 3).
      const cur = condition.value;
      const valid =
        typeof cur === "number"
          ? cur > 0
          : typeof cur === "string" &&
            /^\d+$/.test(cur.trim()) &&
            Number(cur) > 0;
      onChange({ ...condition, operator, value: valid ? cur : "30" });
    } else if (
      condition.operator === "days_ago" ||
      condition.operator === "days_in_future" ||
      condition.operator === "previous_calendar_days"
    ) {
      // Switching OUT of a day-count operator into a normal date/number
      // comparison: drop the day-count integer so it doesn't get treated
      // as a date or numeric value by the next operator's compiler branch.
      onChange({ ...condition, operator, value: "" });
    } else if (isBehavioralField(condition.field)) {
      // For behavioral fields, convert value format between equals (string) and within_days (array)
      const currentEventType = Array.isArray(condition.value)
        ? String(condition.value[0] ?? "")
        : (condition.value as string) || "";
      if (operator === "within_days") {
        onChange({ ...condition, operator, value: [currentEventType, "30"] });
      } else {
        onChange({ ...condition, operator, value: currentEventType });
      }
    } else if (operator === "in" || operator === "not_in") {
      // Coerce value to string[]. Scalar string → single-element array
      // (or empty array if value was blank). Already-array stays.
      const next = Array.isArray(condition.value)
        ? condition.value
        : typeof condition.value === "string" && condition.value.trim()
          ? [condition.value.trim()]
          : typeof condition.value === "number"
            ? [String(condition.value)]
            : [];
      onChange({ ...condition, operator, value: next });
    } else if (
      Array.isArray(condition.value) &&
      condition.operator !== "between"
    ) {
      // Switching from in/not_in → scalar operator: take the first element.
      // Loses additional values silently — acceptable since the marketer
      // can re-pick. Alternative would be to join with commas, but that
      // produces a string the equals/contains/etc. operators can't match.
      onChange({
        ...condition,
        operator,
        value: condition.value.length > 0 ? condition.value[0] : "",
      });
    } else {
      onChange({ ...condition, operator });
    }
  };

  const handleValueChange = (value: any) => {
    onChange({ ...condition, value });
  };

  const showValueInput = !["is_null", "is_not_null"].includes(
    condition.operator,
  );

  // Render the appropriate value input based on field type
  const renderValueInput = () => {
    if (!showValueInput) {
      return null;
    }

    // Zuora entitlement: multi-select over the live catalog, so an operator can only pick values
    // that actually exist on current active subscriptions.
    if (getFieldType(condition.field) === "zuora") {
      return (
        <ZuoraValueInput
          field={condition.field as string}
          value={condition.value}
          onChange={handleValueChange}
        />
      );
    }

    // Proximity/radius field: dedicated editor (ZIP text or debounced org/location search + miles).
    if (getFieldType(condition.field) === "proximity") {
      return (
        <ProximityValueInput
          operator={condition.operator}
          value={condition.value}
          onChange={handleValueChange}
        />
      );
    }

    // Custom-attribute value override (2026-05-26): when the field is a
    // `custom:<slug>` whose slug appears in customAttributeOverrides,
    // render a dropdown / checkbox group sourced from the configured
    // finite value list instead of the default free-text or generic chip
    // input. Falls through to existing rendering for any unconfigured
    // custom attribute so text attrs remain free text.
    const customOverride = getCustomAttributeOverride(
      condition.field as string,
    );
    if (customOverride) {
      if (
        condition.operator === "equals" ||
        condition.operator === "not_equals"
      ) {
        const scalarValue =
          typeof condition.value === "string" ? condition.value : "";
        return (
          <select
            value={scalarValue}
            onChange={(e) => handleValueChange(e.target.value)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[150px]"
          >
            <option value="">Select...</option>
            {customOverride.values.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        );
      }
      if (condition.operator === "in" || condition.operator === "not_in") {
        // Checkbox group constrained to the configured option list.
        // Scalar→array coercion already happens in handleOperatorChange
        // when switching from equals; defend against any other entry
        // path by treating non-array values as empty.
        const selected = Array.isArray(condition.value)
          ? (condition.value as string[])
          : [];
        const toggle = (v: string) => {
          const next = selected.includes(v)
            ? selected.filter((x) => x !== v)
            : [...selected, v];
          handleValueChange(next);
        };
        return (
          <div className="flex flex-wrap items-center gap-3 px-2 py-1 border border-gray-300 rounded text-sm min-w-[200px]">
            {customOverride.values.map((v) => (
              <label
                key={v}
                className="inline-flex items-center gap-1 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(v)}
                  onChange={() => toggle(v)}
                  className="h-3.5 w-3.5"
                />
                <span>{v}</span>
              </label>
            ))}
          </div>
        );
      }
      // Other operators (contains, starts_with, etc.) for an override
      // field: rare and probably operator-misuse for a finite-vocabulary
      // attribute, but don't block — fall through to default text input
      // so the marketer is not stuck.
    }

    // email_quality in / not_in: constrain to the 4 finite buckets via a
    // checkbox group (matches the custom-override pattern) so the choice is
    // obvious rather than free-text chips. Only 4 options, so no state-list
    // sprawl. Scalar→array coercion already happens in handleOperatorChange.
    if (
      (condition.field === "email_quality" ||
        condition.field === "account_region" ||
        condition.field === "account_type" ||
        condition.field === "account_status") &&
      (condition.operator === "in" || condition.operator === "not_in")
    ) {
      const selected = Array.isArray(condition.value)
        ? (condition.value as string[])
        : [];
      const toggle = (v: string) => {
        const next = selected.includes(v)
          ? selected.filter((x) => x !== v)
          : [...selected, v];
        handleValueChange(next);
      };
      return (
        <div className="flex flex-wrap items-center gap-3 px-2 py-1 border border-gray-300 rounded text-sm min-w-[200px]">
          {selectOptions.map((opt) => (
            <label
              key={opt.value}
              className="inline-flex items-center gap-1 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="h-3.5 w-3.5"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }

    // in / not_in: multi-value chip input. Operator-level branch wins over
    // field-type so all in/not_in renders consistently regardless of whether
    // the field is text / number / uuid.
    if (
      (condition.operator === "in" || condition.operator === "not_in") &&
      !isBehavioralField(condition.field)
    ) {
      const values = Array.isArray(condition.value)
        ? (condition.value as string[])
        : [];
      return (
        <ChipMultiValueInput
          values={values}
          onChange={(next) => handleValueChange(next)}
          placeholder="Type a value and press Enter or comma…"
          fieldType={fieldType}
        />
      );
    }

    // days_ago / days_in_future: the SQL compiler builds
    // `INTERVAL '<value> days'` from this input, so the value must be an
    // integer day count, NOT a date. Audit AI 2026-05-20 caught this — we
    // exposed these operators on date fields but renderValueInput was
    // still emitting <input type="date">, which would send '2026-05-20'
    // and produce `INTERVAL '2026-05-20 days'` (invalid).
    if (
      condition.operator === "days_ago" ||
      condition.operator === "days_in_future" ||
      condition.operator === "previous_calendar_days"
    ) {
      return (
        <input
          type="number"
          min={1}
          step={1}
          value={(condition.value as string | number | undefined) ?? ""}
          onChange={(e) => handleValueChange(e.target.value)}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[100px]"
          placeholder="N days"
        />
      );
    }

    switch (fieldType) {
      case "boolean":
        return (
          <select
            value={
              condition.value === true
                ? "true"
                : condition.value === false
                  ? "false"
                  : ""
            }
            onChange={(e) => handleValueChange(e.target.value === "true")}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[100px]"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case "date":
        return (
          <input
            type="date"
            value={scalarInputValue(condition.value)}
            onChange={(e) => handleValueChange(e.target.value)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
          />
        );

      case "select":
        return (
          <select
            value={scalarInputValue(condition.value)}
            onChange={(e) => handleValueChange(e.target.value)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[150px]"
          >
            <option value="">Select...</option>
            {selectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "number":
        return (
          <input
            type="number"
            value={scalarInputValue(condition.value)}
            onChange={(e) =>
              handleValueChange(e.target.value ? Number(e.target.value) : "")
            }
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
            placeholder="Enter number..."
          />
        );

      case "behavioral": {
        // For behavioral fields: show event type dropdown + optional days input
        const currentEventType = Array.isArray(condition.value)
          ? condition.value[0] || ""
          : (condition.value as string) || "";
        const currentDays = Array.isArray(condition.value)
          ? condition.value[1] || ""
          : "";

        return (
          <>
            <select
              value={currentEventType}
              onChange={(e) => {
                if (condition.operator === "within_days") {
                  handleValueChange([e.target.value, currentDays]);
                } else {
                  handleValueChange(e.target.value);
                }
              }}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm min-w-[160px]"
            >
              <option value="">Select event type...</option>
              {CONTACT_EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {condition.operator === "within_days" && (
              <input
                type="number"
                value={currentDays}
                onChange={(e) =>
                  handleValueChange([currentEventType, e.target.value])
                }
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Days"
                min="1"
              />
            )}
          </>
        );
      }

      default:
        return (
          <input
            type="text"
            value={scalarInputValue(condition.value)}
            onChange={(e) => handleValueChange(e.target.value)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
            placeholder="Enter value..."
          />
        );
    }
  };

  return (
    <div className="flex items-center gap-2 bg-white p-3 rounded border border-gray-200 flex-wrap">
      <select
        value={condition.field}
        onChange={(e) => handleFieldChange(e.target.value as QueryField)}
        className="px-2 py-1 border border-gray-300 rounded text-sm min-w-[150px]"
      >
        {allFieldOptions
          .filter((opt) => !excludeFields?.includes(opt.value))
          .map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
      </select>

      {customAttrLoading ? (
        // Loading custom-attribute definitions: don't let the user pick
        // an operator yet — getFieldType would fall back to 'text' and
        // expose wrong operators for boolean/date attributes. Audit AI
        // 2026-05-20 should-fix.
        <span className="px-2 py-1 text-sm text-gray-500 italic">
          Loading attribute definitions…
        </span>
      ) : customAttrErrored ? (
        <span className="px-2 py-1 text-sm text-red-600">
          Failed to load custom attributes. Pick a built-in field, or reload the
          page.
        </span>
      ) : (
        <>
          <select
            value={condition.operator}
            onChange={(e) =>
              handleOperatorChange(e.target.value as FieldOperator)
            }
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            {operators.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {renderValueInput()}
        </>
      )}

      <button
        type="button"
        onClick={onRemove}
        className="text-red-600 hover:text-red-800 text-sm font-medium ml-auto shrink-0"
      >
        Remove
      </button>

      {/* Inline field help (e.g. Zuora Account fields: shared account-level value +
          "add Is Primary Member = Yes for one recipient per account"). */}
      {(() => {
        const help = allFieldOptions.find(
          (o) => o.value === condition.field,
        )?.help;
        if (!help) {
          return null;
        }
        return (
          <p className="w-full text-xs text-gray-500 italic mt-1">{help}</p>
        );
      })()}

      {/* Staleness note for Pardot-sourced attributes. Audit AI 2026-05-23
          Option H: surface the "as fresh as last webhook" expectation so
          marketers don't expect counts to match Pardot's UI in real-time. */}
      {isCustomField &&
        typeof condition.field === "string" &&
        condition.field.startsWith("custom:pardot_") && (
          <p className="w-full text-xs text-amber-700 italic mt-1">
            Note: Pardot-sourced attributes reflect the last webhook delivery
            for each contact. Segment counts may lag behind Pardot's current
            view.
          </p>
        )}
    </div>
  );
};

// ============================================================================
/**
 * ZuoraValueInput — controlled multi-select over live Zuora values.
 *
 * Values are exact strings: "Monthly Emergency Shield" and "Monthly Emergency Shield - Secondary"
 * are separate plans and never match one another. The catalog comes from the same
 * active-subscription source the segment compiler evaluates against.
 */
const ZuoraValueInput: React.FC<{
  field: string;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, value, onChange }) => {
  const [options, setOptions] = useState<ZuoraEntitlementValue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const kind = ZUORA_FIELD_KIND[field] ?? "plan";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await getZuoraEntitlementValues(kind);
        if (!cancelled) {
          setOptions(rows);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load values",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const selected = Array.isArray(value)
    ? (value as unknown[]).filter((v): v is string => typeof v === "string")
    : typeof value === "string" && value
      ? [value]
      : [];

  // Typing "defend" brings Annual Defend Plan and Monthly Defend Plan together, which an
  // alphabetical list of 355 values cannot do — they sit at opposite ends of it.
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? options.filter(
        (o) =>
          o.value.toLowerCase().includes(needle) || selected.includes(o.value), // never hide something already chosen
      )
    : options;

  return (
    <div className="flex flex-col gap-1">
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Type to filter, e.g. defend"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
      />
      <select
        multiple
        size={8}
        value={selected}
        onChange={(e) =>
          onChange(Array.from(e.target.selectedOptions).map((o) => o.value))
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
      >
        {visible.map((o) => (
          <option key={o.value} value={o.value}>
            {o.value} ({o.contact_count.toLocaleString()})
          </option>
        ))}
      </select>
      <span className="text-xs text-gray-500">
        {error
          ? `Could not load values: ${error}`
          : `Hold ⌘/Ctrl to select several. ${
              needle
                ? `${visible.length} of ${options.length} shown`
                : `${options.length} values, A–Z`
            }. Counts are contacts with a current active subscription.`}
      </span>
      {selected.length > 0 && (
        <span className="text-xs text-gray-600">
          Selected: {selected.join(", ")}
        </span>
      )}
    </div>
  );
};

// ChipMultiValueInput — multi-value input for `in` / `not_in` operators
// ============================================================================
//
// Stores values as string[]. User types a value and presses Enter or comma
// to add. Existing chips can be removed via the × button. Empty input on
// Backspace removes the last chip. Trim + dedupe applied on add.
//
// For numeric/uuid fields the user still types as strings — the service
// layer at send-time coerces. We don't enforce numeric input here because
// users mid-edit might type "1, 2, 3" and we'd rather accept it.

interface ChipMultiValueInputProps {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  fieldType?: string;
}

const ChipMultiValueInput: React.FC<ChipMultiValueInputProps> = ({
  values,
  onChange,
  placeholder,
}) => {
  const [draft, setDraft] = React.useState("");

  const commit = (raw: string) => {
    // Allow comma-separated paste: split, trim, drop blanks + dupes
    const additions = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .filter((s) => !values.includes(s));
    if (additions.length > 0) {
      onChange([...values, ...additions]);
    }
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex-1 min-w-[200px] flex items-center gap-1 flex-wrap px-2 py-1 border border-gray-300 rounded bg-white">
      {values.map((v, idx) => (
        <span
          key={`${v}-${idx}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
        >
          {v}
          <button
            type="button"
            onClick={() => removeAt(idx)}
            className="text-blue-600 hover:text-blue-900 leading-none"
            aria-label={`Remove ${v}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (draft.trim()) {
            commit(draft);
          }
        }}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] text-sm border-none outline-none focus:ring-0 bg-transparent"
      />
    </div>
  );
};

export default QueryBuilder;
